import React from "react";
import PropTypes from "prop-types";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import Select from "antd/lib/select";
import PlainButton from "@/components/PlainButton";
import ParameterValueInput from "@/components/ParameterValueInput";
import { normalizeDashboardTabAssignment } from "@/services/dashboard";

import "@/components/Parameters.less";

/**
 * Single global parameter row inside a dashboard bar; draggable via grip when dragEnabled.
 */
export default function DashboardSortableParamRow({
  dragId,
  dragEnabled,
  param,
  paramIndex,
  editable,
  parameterTabAssignmentOptions,
  onSetPendingValue,
  onShowParameterSettings,
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
    disabled: !dragEnabled,
    data: {
      label: param.title || param.name,
      type: "dashboard-global-param",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.35 } : {}),
    zIndex: isDragging ? 2 : undefined,
  };

  const { onPointerDown: opd, ...listenerRest } = listeners || {};

  const handlePointerDown = opd
    ? event => {
        opd(event);
        event.stopPropagation();
      }
    : undefined;

  let parameterTabsMultiValue = null;
  if (editable && parameterTabAssignmentOptions?.tabs?.length > 0) {
    parameterTabsMultiValue = normalizeDashboardTabAssignment(parameterTabAssignmentOptions.assignments[param.name]);
    if (!parameterTabsMultiValue.length) {
      parameterTabsMultiValue = [
        parameterTabAssignmentOptions.fallbackTabId ?? parameterTabAssignmentOptions.tabs[0].id,
      ];
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="parameter-block di-block"
      data-editable={dragEnabled ? true : undefined}
      data-test={`ParameterBlock-${param.name}`}>
      {dragEnabled && (
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="dashboard-param-dnd-grip drag-handle btn btn-xs btn-link p-l-5 p-r-5"
          title="Drag to reorder or move to another parameters bar"
          aria-label="Drag parameter"
          data-test={`DashboardParamDragGrip-${param.name}`}
          {...attributes}
          {...listenerRest}
          {...(handlePointerDown ? { onPointerDown: handlePointerDown } : {})}
          onMouseDown={event => event.stopPropagation()}>
          <i className="fa fa-bars" aria-hidden />
        </button>
      )}
      <div className="di-block" data-test={`ParameterName-${param.name}`}>
        <div className="parameter-heading">
          <label>{param.title || param.name}</label>
          {editable && (
            <PlainButton
              className="btn btn-default btn-xs m-l-5"
              aria-label="Edit"
              type="button"
              onClick={() => onShowParameterSettings(param, paramIndex)}
              data-test={`ParameterSettings-${param.name}`}>
              <i className="fa fa-cog" aria-hidden="true" />
            </PlainButton>
          )}
          {parameterTabsMultiValue !== null && (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              size="small"
              className="dashboard-parameter-tab-assign-select m-l-5"
              dropdownMatchSelectWidth={false}
              value={parameterTabsMultiValue}
              placeholder="Tabs"
              onClick={event => event.stopPropagation()}
              onMouseDown={event => event.stopPropagation()}
              aria-label="Parameter tabs"
              onChange={tabIds => parameterTabAssignmentOptions.onAssignmentChange(param.name, tabIds)}
              options={parameterTabAssignmentOptions.tabs.map(tab => ({
                label: tab.name,
                value: tab.id,
              }))}
            />
          )}
        </div>

        <ParameterValueInput
          type={param.type}
          value={param.normalizedValue}
          parameter={param}
          enumOptions={param.enumOptions}
          queryId={param.queryId}
          onSelect={(value, isDirty) => onSetPendingValue(param, value, isDirty)}
          regex={param.regex}
        />
      </div>
    </div>
  );
}

DashboardSortableParamRow.propTypes = {
  dragId: PropTypes.string.isRequired,
  dragEnabled: PropTypes.bool.isRequired,
  param: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  paramIndex: PropTypes.number.isRequired,
  editable: PropTypes.bool,
  parameterTabAssignmentOptions: PropTypes.shape({
    tabs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      })
    ),
    assignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    fallbackTabId: PropTypes.string,
    onAssignmentChange: PropTypes.func,
  }),
  onSetPendingValue: PropTypes.func.isRequired,
  onShowParameterSettings: PropTypes.func.isRequired,
};

DashboardSortableParamRow.defaultProps = {
  editable: false,
  parameterTabAssignmentOptions: null,
};
